// A believable month in Gulmohar Colony, written as real signed claims through the real policy.
// Every line below goes through Scope.submit; the seed has no back door.

import { marketplacePolicy, sign } from '@model/index.ts';
import { DAY, HOUR, MIN } from './world.ts';
import type { World } from './world.ts';
import { caseAudience } from './queries.ts';

export async function seed(w: World): Promise<void> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Local clock time on a past day. Every seeded event is at least a day old, so it is always in the past.
  const at = (daysAgo: number, hh: number, mm = 0): number => today.getTime() - daysAgo * DAY + hh * HOUR + mm * MIN;
  const id = (k: string): string => w.p(k).id;

  const policy = marketplacePolicy({ phoneIssuers: [id('phone')], stewards: [id('kavita'), id('joseph')], triage: [id('triage')] });
  const genesis = await sign(w.p('community').actor, { predicate: 'policy', body: policy as never, issuedAt: at(40, 9) });
  await w.genesis(genesis, at(40, 9));

  // Verify and forget: the issuer keeps a keyed fingerprint, signs an empty attestation, drops the number.
  const viaPhone = async (k: string, digits: string, t: number): Promise<void> => {
    w.phones.push(await w.phoneFingerprint(digits));
    await w.act('phone', { predicate: 'verified.phone', subject: id(k) }, t);
    await w.act(k, { predicate: 'join', subject: id('community') }, t + 2 * MIN);
  };
  await viaPhone('kavita', '9820011122', at(39, 10, 5));
  await viaPhone('ramesh', '9811122233', at(38, 11, 40));
  await viaPhone('joseph', '9821234567', at(37, 17, 45));
  await viaPhone('mohan', '9811144455', at(36, 18, 15));
  await viaPhone('gurpreet', '9899000011', at(33, 9, 30));
  await viaPhone('farida', '9871234560', at(30, 15, 10));
  await w.act('gurpreet', { predicate: 'vouch', subject: id('sunita') }, at(28, 19, 0));
  await w.act('sunita', { predicate: 'join', subject: id('community') }, at(28, 19, 25));

  // The one completed trade: Ramesh's almirah to Mohan, rated and answered on the record.
  const almirah = await w.act(
    'ramesh',
    { predicate: 'offer', body: { title: 'Steel almirah, 2 doors', priceInr: 4500, details: 'Godrej, 6 feet. Scratches on one side. Lock and key work.' } },
    at(21, 10, 0),
  );
  const deal = await w.act('mohan', { predicate: 'accept', refs: [almirah.claim.id] }, at(20, 17, 30));
  await w.act('mohan', { predicate: 'complete', refs: [deal.claim.id] }, at(19, 12, 10));
  await w.act('ramesh', { predicate: 'complete', refs: [deal.claim.id] }, at(19, 12, 40));
  const said = await w.act(
    'mohan',
    { predicate: 'rate', subject: id('ramesh'), refs: [deal.claim.id], body: { score: 5, text: 'He brought it to my door himself and fitted it.' } },
    at(19, 13, 0),
  );
  await w.act('ramesh', { predicate: 'respond', refs: [said.claim.id], body: { text: 'Thank you, Mohan ji. Use it well.' } }, at(19, 20, 15));

  const offer = (k: string, title: string, priceInr: number, details: string, t: number) =>
    w.act(k, { predicate: 'offer', body: { title, priceInr, details } }, t);
  const cooker = await offer('ramesh', 'Pressure cooker, 5 litre', 900, 'Hawkins. New gasket fitted last month.', at(15, 11, 0));
  const fan = await offer('ramesh', 'Ceiling fan, 48 inch', 1200, 'Usha. Used two years, runs quietly. I can help fit it.', at(15, 11, 20));
  await offer('gurpreet', 'Class 10 NCERT books, full set', 600, 'All subjects, 2025 edition. A few pencil marks.', at(12, 18, 0));
  await offer('farida', 'Singer sewing machine', 3500, 'Foot pedal model. Serviced in August. Comes with the cover.', at(11, 16, 45));
  await offer('sunita', 'Tulsi and money plant saplings', 50, 'In small pots. Pick up from my gate any evening.', at(9, 17, 30));

  await viaPhone('vikram', '9990001234', at(7, 21, 10));
  const phone = await offer('vikram', 'iPhone 13, 128 GB', 18000, 'Sealed box. Bill available. Urgent sale.', at(6, 22, 5));

  // A stranger's unbound rating: refused by the law, kept as a refused record.
  await w.act('vikram', { predicate: 'rate', subject: id('ramesh'), body: { score: 1, text: 'Overpriced.' } }, at(6, 23, 0));

  await viaPhone('ananya', '9567012345', at(5, 14, 0));
  await offer('ananya', 'Engineering drawing kit', 350, 'Mini drafter, set squares and compass. Used one semester.', at(5, 16, 20));
  await offer('ananya', 'Hostel cooler', 2800, 'Symphony, 25 litre. Works well. Moving out of the hostel.', at(4, 11, 0));

  // Gurpreet asks Vikram about the phone. The inquiry is on the record for two people only;
  // the messages themselves stay on their phones.
  const g = await w.act('gurpreet', { predicate: 'inquire', refs: [phone.claim.id], audience: [id('gurpreet'), id('vikram')] }, at(4, 19, 0));
  await w.message('gurpreet', id('vikram'), g.claim.id, 'Namaste. Can I see the phone before paying?', at(4, 19, 1));
  await w.message('vikram', id('gurpreet'), g.claim.id, 'Pay ₹2,000 advance on UPI first. Many people are asking.', at(4, 19, 20));
  await w.message('gurpreet', id('vikram'), g.claim.id, 'I will pay only after I see it.', at(4, 19, 40));
  await w.act(
    'gurpreet',
    {
      predicate: 'report',
      subject: id('vikram'),
      audience: caseAudience(w, id('gurpreet')),
      body: { reason: 'advance', details: 'Asked for ₹2,000 on UPI before showing the phone.' },
    },
    at(3, 10, 0),
  );

  // Mohan agreed to buy the cooker four days ago and has not come for it. Neither has confirmed (the release journey).
  await w.act('mohan', { predicate: 'accept', refs: [cooker.claim.id] }, at(4, 10, 30));

  // Sunita asks Ramesh about the fan.
  const s = await w.act('sunita', { predicate: 'inquire', refs: [fan.claim.id], audience: [id('sunita'), id('ramesh')] }, at(2, 18, 0));
  await w.message('sunita', id('ramesh'), s.claim.id, 'Can you fit it on Sunday?', at(2, 18, 1));
  await w.message('ramesh', id('sunita'), s.claim.id, 'Yes, after 5 pm. Shop closes early on Sunday.', at(2, 18, 30));

  // Farida asks too. This is the message she may later share as proof.
  const f = await w.act('farida', { predicate: 'inquire', refs: [phone.claim.id], audience: [id('farida'), id('vikram')] }, at(1, 18, 0));
  await w.message('farida', id('vikram'), f.claim.id, 'Is the iPhone still available?', at(1, 18, 1));
  await w.message('vikram', id('farida'), f.claim.id, 'Yes. Send ₹2,000 advance to hold it, otherwise someone else will take it.', at(1, 18, 12));
  await w.message('farida', id('vikram'), f.claim.id, 'Let me think about it.', at(1, 18, 30));
}
